// Help & FAQ — built to the pplkpr web design language (WebAurora bg, glass
// panels, pill tabs, search, accordion). Standalone public page. The support
// email is served from the backend (getSupportEmailEurW) so it stays out of
// the static bundle and can't be scraped.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getSupportEmailEurW } from '../firebaseConfig';

const P = {
  textMuted: 'rgba(220, 200, 240, 0.65)',
  textDim: 'rgba(200, 180, 220, 0.45)',
};

const fLink = {
  color: '#f0abfc', textDecoration: 'none',
  borderBottom: '1px dashed rgba(240, 171, 252, 0.4)',
};
const fList = { margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.7, color: P.textMuted };

// ─── Icons ───────────────────────────────────────────────────
function Logo({ s = 28 }) {
  return (
    <svg width={s * 1.05} height={s} viewBox="0 0 28 26" fill="none">
      <path d="M3 6c0-1.6 1.3-3 3-3s3 1.4 3 3v10l-3 2v-3H6c-1.7 0-3-1.3-3-3V6Z" fill="#7c3aed"/>
      <circle cx="6" cy="3" r="2.5" fill="#7c3aed"/>
      <path d="M11 9c0-1.6 1.3-3 3-3s3 1.4 3 3v8c0 1.6-1.3 3-3 3h-1l-3 2v-3c-1.6 0-3-1.4-3-3V9Z" fill="#d946ef"/>
      <circle cx="14" cy="6" r="2.5" fill="#d946ef"/>
      <circle cx="11.5" cy="13.5" r="0.9" fill="#fff"/>
      <circle cx="14" cy="13.5" r="0.9" fill="#fff"/>
      <circle cx="16.5" cy="13.5" r="0.9" fill="#fff"/>
    </svg>
  );
}
function SendIcon({ s = 14, c = '#fff' }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 12L21 4l-4 18-5-7-9-3Z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── FAQ data ────────────────────────────────────────────────
const FAQ_SECTIONS = [
  {
    id: 'about',
    label: 'About pplkpr',
    items: [
      {
        q: 'What is pplkpr?',
        a: (
          <>
            <p>
              pplkpr (<em>&ldquo;people keeper&rdquo;</em>) is a place to build meaningful
              relationships — with the help of AI. Meet new people in circles, engage in real
              conversations, voice your opinion, strengthen the friendships you already have,
              and stay close to the people who matter.
            </p>
            <p>
              <b>f8</b>, your AI companion, is there along the way for reminders, conversation
              starters, advice, and a private place to think things through.
            </p>
          </>
        ),
      },
      {
        q: 'Who is pplkpr for?',
        a: (
          <p>
            Anyone who wants more meaningful relationships in their life — people they already
            know and people they haven&rsquo;t met yet. Whether you&rsquo;re trying to stay
            close to old friends, deepen the relationships you have, or find new people who
            care about the same things you do, pplkpr is built for you.
          </p>
        ),
      },
    ],
  },
  {
    id: 'f8',
    label: 'f8, your AI companion',
    items: [
      {
        q: 'Who is f8?',
        a: (
          <p>
            f8 is your AI relationship companion. Chat with f8 by text or voice for advice,
            help drafting a message, a nudge to reach out to someone, or just to talk through
            what&rsquo;s going on with the people in your life.
          </p>
        ),
      },
      {
        q: 'What can I ask f8?',
        a: (
          <>
            <p>Anything that helps you show up for the people in your life. Common asks:</p>
            <ul style={fList}>
              <li>&ldquo;Help me write a thoughtful birthday message&rdquo;</li>
              <li>&ldquo;What should I ask my dad about his trip?&rdquo;</li>
              <li>&ldquo;I had a hard conversation with my partner — can we talk it through?&rdquo;</li>
              <li>&ldquo;Who haven&rsquo;t I caught up with in a while?&rdquo;</li>
            </ul>
          </>
        ),
      },
      {
        q: 'Can I talk to f8 by voice?',
        a: (
          <p>
            Yes. You can chat with f8 by text or voice. Voice conversations use monthly minutes
            that are included with <b>Premium</b>.
          </p>
        ),
      },
      {
        q: 'Does f8 remember the people in my life?',
        a: (
          <p>
            Yes. f8 uses what you&rsquo;ve added about your contacts — names, how you know them,
            recent interactions, things they care about — so its suggestions and reminders feel
            personal instead of generic. You stay in control of what gets remembered and can
            edit or remove anything in <b>Settings</b>.
          </p>
        ),
      },
    ],
  },
  {
    id: 'circles',
    label: 'Circles',
    items: [
      {
        q: 'What are circles?',
        a: (
          <p>
            Circles are small, interest-based community groups — up to <b>50 members</b> —
            built for real conversations. No public feeds, no follower counts, no chasing
            likes. Just a handful of people who care about the same thing.
          </p>
        ),
      },
      {
        q: 'How do I join or leave a circle?',
        a: (
          <p>
            Browse circles in the app and request to join the ones that interest you. You can
            leave any circle at any time from its settings — your past messages stay in the
            circle&rsquo;s history unless you delete them.
          </p>
        ),
      },
      {
        q: 'How is content in circles moderated?',
        a: (
          <p>
            We moderate content posted in circles to keep conversations safe and on-topic.
            Posts that violate our community guidelines — harassment, hate speech, illegal
            content, spam, sexual content involving minors — are removed, and repeat offenders
            lose access to circles or to pplkpr entirely. You can report any post directly from
            the app, and we review every report.
          </p>
        ),
      },
    ],
  },
  {
    id: 'safety',
    label: 'Safety & privacy',
    items: [
      {
        q: 'Is my data private?',
        a: (
          <p>
            Your relationships are private by default. The people you add, your notes about
            them, your reminders, and your conversations with f8 are visible only to you.
          </p>
        ),
      },
      {
        q: 'Do you train AI on my conversations?',
        a: (
          <p>
            No. Your chats with f8 and your private notes about the people in your life are{' '}
            <b>not</b> used to train AI models.
          </p>
        ),
      },
      {
        q: 'How does pplkpr handle spam and unwanted messages?',
        a: (
          <p>
            We use proactive spam protection in direct messages: if a sender&rsquo;s behavior
            looks like spam or abuse, both people see a warning before the conversation
            continues, and you can block or report from that warning. Repeated spam or abusive
            behavior results in account restrictions.
          </p>
        ),
      },
      {
        q: 'Can I export or delete my data?',
        a: (
          <p>
            Yes. You can request an export of your data, and you can delete your account at any
            time from <b>Settings → Account</b>. Deleting your account removes your contacts,
            notes, conversations with f8, and circle memberships from our systems.
          </p>
        ),
      },
    ],
  },
  {
    id: 'premium',
    label: 'Premium & account',
    items: [
      {
        q: 'Is pplkpr free?',
        a: (
          <p>
            Yes. pplkpr is free to use with daily limits. <b>Premium</b> removes the text-chat
            cap with f8, adds monthly voice minutes with f8, and lets you see who has viewed
            your profile.
          </p>
        ),
      },
      {
        q: 'How do I manage or cancel Premium?',
        a: (
          <p>
            Open <b>Settings → Subscription</b> to view or cancel your plan. A cancelled plan
            stays active until the end of the period you&rsquo;ve already paid for — there&rsquo;s
            no early cut-off.
          </p>
        ),
      },
      {
        q: 'I need help with something not on this list — how do I reach you?',
        a: (
          <p>
            In the app, tap <b>Settings → Help</b> — we read every message. On the web, use the
            support email below.
          </p>
        ),
      },
    ],
  },
];

// ─── Pieces ──────────────────────────────────────────────────
function PrimaryButton({ children, onClick, Icon }) {
  return (
    <button onClick={onClick} style={{
      appearance: 'none', cursor: 'pointer', border: 0,
      padding: '10px 18px', borderRadius: 12,
      background: 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)',
      color: '#fff', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
      display: 'inline-flex', alignItems: 'center', gap: 8,
      boxShadow: '0 4px 16px rgba(217, 70, 239, 0.32)', whiteSpace: 'nowrap',
    }}>
      {Icon && <Icon s={14} c="#fff" />}
      <span>{children}</span>
    </button>
  );
}

function PillTabs({ tabs, value, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 4, padding: 4,
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 12, flexWrap: 'wrap',
    }}>
      {tabs.map((t) => {
        const active = t.id === value;
        return (
          <button key={t.id} onClick={() => onChange(t.id)} style={{
            appearance: 'none', cursor: 'pointer', border: 0,
            padding: '8px 14px', borderRadius: 9,
            background: active ? 'rgba(217, 70, 239, 0.18)' : 'transparent',
            color: active ? '#fff' : P.textMuted,
            fontSize: 13, fontWeight: active ? 600 : 500,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
          }}>
            <span>{t.label}</span>
            <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7 }}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
      <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke={P.textDim} strokeWidth="1.8"/>
          <path d="M21 21l-5-5" stroke={P.textDim} strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 14px 10px 38px', borderRadius: 12,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          color: '#fff', fontSize: 16, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function FaqItem({ q, a, open, onToggle }) {
  return (
    <div style={{
      borderRadius: 16, overflow: 'hidden',
      background: open ? 'rgba(217, 70, 239, 0.06)' : 'rgba(255, 255, 255, 0.04)',
      border: open ? '1px solid rgba(217, 70, 239, 0.30)' : '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)',
      transition: 'all 0.18s ease',
    }}>
      <button onClick={onToggle} aria-expanded={open} style={{
        appearance: 'none', border: 0, cursor: 'pointer',
        width: '100%', padding: '16px 18px', background: 'transparent',
        color: '#fff', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, textAlign: 'left',
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{q}</span>
        <span aria-hidden style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: 999,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#f0abfc' : P.textMuted, fontSize: 17, lineHeight: 1,
          transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
        }}>+</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', color: P.textMuted, fontSize: 14, lineHeight: 1.65, animation: 'fadeDown 0.2s ease' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 14px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{a}</div>
        </div>
      )}
    </div>
  );
}

function StillNeedHelp() {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const reveal = async () => {
    if (email || loading) return;
    setLoading(true);
    setErr(false);
    try {
      const res = await getSupportEmailEurW({});
      if (res?.data?.email) setEmail(res.data.email);
      else setErr(true);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{
      marginTop: 36, borderRadius: 20, overflow: 'hidden',
      background: 'linear-gradient(135deg, rgba(217, 70, 239, 0.10) 0%, rgba(124, 58, 237, 0.08) 100%)',
      border: '1px solid rgba(217, 70, 239, 0.22)',
      backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)',
      padding: '24px', position: 'relative',
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.6,
        background:
          'radial-gradient(360px 200px at 20% 0%, rgba(217, 70, 239, 0.14), transparent 60%),' +
          'radial-gradient(360px 200px at 100% 100%, rgba(124, 58, 237, 0.16), transparent 60%)',
      }} />
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 280px' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: '#fff' }}>Still need help?</h3>
          <p style={{ margin: '6px 0 0', color: P.textMuted, fontSize: 13.5, lineHeight: 1.55 }}>
            In the app, tap <b style={{ color: '#fff' }}>Settings → Help</b> — we read every message. On the web, drop us a line.
          </p>
          {err && (
            <p style={{ color: '#fca5a5', fontSize: 12.5, margin: '8px 0 0' }}>
              Couldn&rsquo;t load the email right now — please try again in a moment.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {email ? (
            <a href={`mailto:${email}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff', fontSize: 13.5, fontWeight: 600, textDecoration: 'none',
            }}>
              <SendIcon s={14} c="#f0abfc" />
              {email}
            </a>
          ) : (
            <PrimaryButton onClick={reveal} Icon={SendIcon}>
              {loading ? 'Loading…' : 'Show support email'}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}

function FaqAurora() {
  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
      <div style={{
        position: 'absolute', top: '-15%', left: '5%', width: '50%', height: '60%', borderRadius: '50%',
        background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)',
        filter: 'blur(80px)', opacity: 0.45, animation: 'pplWA1 22s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', top: '20%', right: '-5%', width: '50%', height: '70%', borderRadius: '50%',
        background: 'radial-gradient(circle, #d946ef 0%, transparent 70%)',
        filter: 'blur(90px)', opacity: 0.3, animation: 'pplWA2 26s ease-in-out infinite',
      }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(14,7,18,0.55) 0%, rgba(14,7,18,0.82) 100%)' }} />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────
export default function Faq() {
  const [activeId, setActiveId] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState({ 'about/0': true });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const q = query.trim().toLowerCase();
  const sectionsRaw = activeId === 'all' ? FAQ_SECTIONS : FAQ_SECTIONS.filter((s) => s.id === activeId);
  const sections = q
    ? sectionsRaw
        .map((s) => ({ ...s, items: s.items.filter((it) => it.q.toLowerCase().includes(q)) }))
        .filter((s) => s.items.length > 0)
    : sectionsRaw;

  const totalQ = FAQ_SECTIONS.reduce((n, s) => n + s.items.length, 0);
  const tabs = [
    { id: 'all', label: 'All', count: totalQ },
    ...FAQ_SECTIONS.map((s) => ({ id: s.id, label: s.label, count: s.items.length })),
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#0e0712', color: '#fff', overflow: 'hidden', fontFamily: 'inherit' }}>
      <FaqAurora />

      {/* Top bar */}
      <header style={{
        position: 'relative', zIndex: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 24px',
        background: 'rgba(20, 10, 30, 0.55)',
        backdropFilter: 'blur(24px) saturate(160%)', WebkitBackdropFilter: 'blur(24px) saturate(160%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo s={28} />
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>pplkpr</span>
          <span style={{ marginLeft: 14, paddingLeft: 14, borderLeft: '1px solid rgba(255,255,255,0.08)', fontSize: 13, color: P.textMuted, fontWeight: 500 }}>Help &amp; FAQ</span>
        </div>
        <Link to="/login" style={{
          color: P.textMuted, textDecoration: 'none', fontSize: 13, fontWeight: 500,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back to sign in</span>
        </Link>
      </header>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', position: 'relative', zIndex: 2, WebkitOverflowScrolling: 'touch' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 24px 60px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.025em', lineHeight: 1 }}>Help &amp; FAQ</h1>
                <span style={{ fontSize: 13, color: P.textDim, fontWeight: 500 }}>{totalQ} answers</span>
              </div>
              <div style={{ fontSize: 13, color: P.textMuted, marginTop: 6 }}>Answers to the things people ask most. Still stuck? Reach out below.</div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <PillTabs tabs={tabs} value={activeId} onChange={setActiveId} />
            <SearchInput value={query} onChange={setQuery} placeholder="Search the FAQ..." />
          </div>

          {/* Sections */}
          {sections.length === 0 ? (
            <div style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16, padding: 28, textAlign: 'center', color: P.textMuted, fontSize: 14,
            }}>
              No matches for &ldquo;<b style={{ color: '#fff' }}>{query}</b>&rdquo;. Try a different word, or clear the search to see the full list.
            </div>
          ) : (
            sections.map((section, sIdx) => (
              <section key={section.id} style={{ marginTop: sIdx === 0 ? 0 : 32 }}>
                {activeId === 'all' && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: '#fff' }}>{section.label}</h2>
                    <span style={{ fontSize: 12, color: P.textDim }}>{section.items.length} {section.items.length === 1 ? 'answer' : 'answers'}</span>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {section.items.map((it, idx) => {
                    const k = `${section.id}/${idx}`;
                    return <FaqItem key={k} q={it.q} a={it.a} open={!!open[k]} onToggle={() => toggle(k)} />;
                  })}
                </div>
              </section>
            ))
          )}

          <StillNeedHelp />

          {/* Footer */}
          <footer style={{
            marginTop: 28, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            color: P.textDim, fontSize: 12, flexWrap: 'wrap', gap: 12,
          }}>
            <span>© pplkpr 2026</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
            </div>
          </footer>
        </div>
      </div>

      <style>{`
        @keyframes pplWA1 { 0%,100%{transform:translate(-10%,-10%) scale(1)} 50%{transform:translate(10%,5%) scale(1.15)} }
        @keyframes pplWA2 { 0%,100%{transform:translate(15%,20%) scale(1.1)} 50%{transform:translate(-5%,-10%) scale(0.95)} }
        @keyframes fadeDown { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
